import { Component, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { CustomButtonComponent } from '../custom-button/custom-button.component';
import { Router, RouterLink, RouterLinkActive } from '@angular/router'
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { DarkModeService } from '../../../services/dark-mode/dark-mode.service';
import { CommonModule } from '@angular/common';
import { UserService } from '../../../services/user/user.service';
import { CashRegisterService } from '../../../services/cash-register/cash-register.service';
import { BrandService } from '../../../services/brand/brand.service';
import { OperatorSessionService } from '../../../services/operator-session/operator-session.service';
import { RecadosService } from '../../../services/recados/recados.service';
import { RecadosHubService } from '../../../services/recados/recados-hub.service';
import { ChatMedicoPainelComponent } from '../chat-medico/chat-medico-painel.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CustomButtonComponent, CommonModule, RouterLink, RouterLinkActive, ChatMedicoPainelComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnDestroy {
  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
    private darkModeService: DarkModeService,
    private userService: UserService,
    private cashRegisterService: CashRegisterService,
    public brandService: BrandService,
    private operatorSessionService: OperatorSessionService,
    private recados: RecadosService,
    private recadosHub: RecadosHubService,
  ) {
    this.userName = (this.localStorageService.get('login').toLowerCase());
    this.unitName = this.localStorageService.get('unitName');
    this.currentMachine = this.userService.getTicketWindow();
    this.userProfile = this.localStorageService.get('profile');
  }

  @Output() menuState = new EventEmitter<boolean>();

  // Hora de inicio da sessao, vinda de quem ja consulta o status ao vivo
  // (o header sozinho nao faz request pra isso em toda tela).
  @Input() onlineDesde: string | null = null;

  userName!: string;
  unitName!: string;
  userProfile!: string;
  currentMachine!: string;

  isAdmin      = false;
  isSupervisor = false;
  hasCashRegisterAccess = false;

  isMenuOpen = false;
  chatAberto = false;
  naoLidas = 0;
  private subNovo?: Subscription;
  private timerNaoLidas?: ReturnType<typeof setTimeout>;

  get naoLidasTexto(): string {
    return this.naoLidas > 99 ? '99+' : String(this.naoLidas);
  }

  // Iniciais do operador para o avatar: primeira letra do nome e do sobrenome
  // quando o login tem separador, senao as duas primeiras do login.
  get iniciais(): string {
    const partes = (this.userName ?? '').split(/[.\s_-]+/).filter(Boolean);
    if (partes.length >= 2) return (partes[0][0] + partes[1][0]).toUpperCase();
    return (this.userName ?? '').slice(0, 2).toUpperCase();
  }

  async ngOnInit() {
    this.isSupervisor = this.userProfile == 'Supervisor';
    this.isAdmin      = this.userProfile == 'Administrador' || this.isSupervisor;
    this.iniciarChat();
    await this.checkCashRegisterAccess();
  }

  // A conexão sobrevive à navegação (o header é recriado a cada página): só o logout a encerra.
  private iniciarChat() {
    this.recadosHub.conectar().catch(() => {});
    this.atualizarNaoLidas();
    this.subNovo = this.recadosHub.novo$.subscribe(() => this.agendarNaoLidas());
  }

  // O total do painel cobre só os últimos 100 recados: o número vem sempre do servidor.
  agendarNaoLidas() {
    clearTimeout(this.timerNaoLidas);
    this.timerNaoLidas = setTimeout(() => this.atualizarNaoLidas(), 2000);
  }

  private async atualizarNaoLidas() {
    try {
      this.naoLidas = await this.recados.naoLidos();
    } catch {
      // mantém o último valor: o chat nunca derruba o cabeçalho
    }
  }

  ngOnDestroy() {
    this.subNovo?.unsubscribe();
    clearTimeout(this.timerNaoLidas);
  }

  toggleChat() {
    this.chatAberto = !this.chatAberto;
    if (this.chatAberto && this.isMenuOpen) {
      this.isMenuOpen = false;
      this.menuState.emit(false);
    }
  }

  private async checkCashRegisterAccess() {
    try {
      const login = this.localStorageService.get('login') as string;
      const unit = Number(this.localStorageService.get('currentUnit'));
      if (!login || !unit) {
        this.hasCashRegisterAccess = false;
        return;
      }
      const caixas = await this.cashRegisterService.getByUnit(unit, login);
      this.hasCashRegisterAccess = caixas.length > 0;
    } catch {
      this.hasCashRegisterAccess = false;
    }
  }

  toggleDarkMode(): void {
    this.darkModeService.toggleDarkMode();
  }

  isDarkMode(): boolean {
    return this.darkModeService.isDarkMode();
  }

  async logout() {
    try {
      await this.userService.logout();
    } catch (e) {
      console.error('Erro ao limpar user log:', e);
    }

    // Le o sessionId do localStorage antes do clear() — depois dele se perde.
    try { await this.operatorSessionService.endSession('logoff'); } catch {}

    try { await this.recadosHub.desconectar(); } catch {}

    this.localStorageService.clear();
    this.router.navigate(['/login']);
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
    if (this.isMenuOpen) this.chatAberto = false;
    this.menuState.emit(this.isMenuOpen);
  }

  // Detectar clique fora do menu
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const targetElement = event.target as HTMLElement;
    // Verifica se o clique foi fora do menu e do botão hamburguer
    if (this.isMenuOpen && !targetElement.closest('.menu') && !targetElement.closest('[data-menu-toggle]')) {
      this.isMenuOpen = false;
    }
  }
}
